import { Component, Input, Output, EventEmitter } from '@angular/core';
import { ScreenAnalyzerService, ScreenAnalysisResult } from '../../services/screen-analyzer.service';
import { NzMessageService } from 'ng-zorro-antd/message';

@Component({
  selector: 'app-auto-screen-generator',
  templateUrl: './auto-screen-generator.component.html',
  styleUrls: ['./auto-screen-generator.component.scss'],
  standalone: false
})
export class AutoScreenGeneratorComponent {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() screenGenerated = new EventEmitter<ScreenAnalysisResult>();

  // Wizard steps
  currentStep = 0;
  steps = [
    { title: 'JSON Input', icon: 'file-text' },
    { title: 'Analysis', icon: 'search' },
    { title: 'Configuration', icon: 'setting' },
    { title: 'Generation', icon: 'rocket' }
  ];

  // Input
  jsonInput = '';
  jsonError = '';

  // Analysis result
  analysisResult: ScreenAnalysisResult | null = null;

  // Configuration
  config = {
    enableMasterDetail: true,
    autoGenerateForms: true, //  NOVO: Gerar formulários automaticamente
    enableCRUD: true,
    enablePagination: true,
    pageSize: 10,
    enableFilters: true,
    enableSorting: true,
    formLayout: 'horizontal' as 'horizontal' | 'vertical',
    selectedEntities: [] as string[]
  };

  constructor(
    private analyzerService: ScreenAnalyzerService,
    private message: NzMessageService
  ) {}

  /**
   * Abre o modal
   */
  open() {
    this.visible = true;
    this.currentStep = 0;
    this.jsonInput = '';
    this.jsonError = '';
    this.analysisResult = null;
    this.visibleChange.emit(true);
  }

  /**
   * Fecha o modal e reseta o estado
   */
  close() {
    this.visible = false;
    this.visibleChange.emit(false);
    
    //  NOVO: Resetar estado para permitir nova geração
    setTimeout(() => {
      this.currentStep = 0;
      this.jsonInput = '';
      this.jsonError = '';
      this.analysisResult = null;
      this.config = {
        enableMasterDetail: true,
        autoGenerateForms: true, //  Resetar para padrão
        enableCRUD: true,
        enablePagination: true,
        pageSize: 10,
        enableFilters: true,
        enableSorting: true,
        formLayout: 'horizontal' as 'horizontal' | 'vertical',
        selectedEntities: []
      };
      console.log('🔄 Estado do Auto Screen Generator resetado');
    }, 300); // Aguardar animação do modal fechar
  }

  /**
   * Próximo passo
   */
  next() {
    if (this.currentStep === 0) {
      // Validar JSON
      if (!this.validateJSON()) return;

      // Analisar JSON
      this.analyzeJSON();
    }

    this.currentStep++;
  }

  /**
   * Passo anterior
   */
  previous() {
    this.currentStep--;
  }

  /**
   *  MELHORADO: Sanitiza JSON removendo caracteres de controle inválidos
   */
  private sanitizeJSON(jsonString: string): string {
    // 1. Remove caracteres de controle inválidos (0x00-0x1F exceto \t, \n, \r)
    let sanitized = jsonString.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '');
    
    // 2. Substituir caracteres especiais problemáticos
    // Substitui quebras de linha literais e tabs por espaços (dentro de strings JSON)
    sanitized = sanitized.replace(/\n/g, ' '); // Substituir quebras de linha por espaço
    sanitized = sanitized.replace(/\r/g, ''); // Remover carriage returns
    sanitized = sanitized.replace(/\t/g, ' '); // Substituir tabs por espaços
    
    // 3. Remover múltiplos espaços seguidos
    sanitized = sanitized.replace(/  +/g, ' ');
    
    console.log('🧹 JSON sanitizado:', {
      tamanhoBefore: jsonString.length,
      tamanhoAfter: sanitized.length,
      caracteresRemovidos: jsonString.length - sanitized.length
    });
    
    return sanitized;
  }

  /**
   * Valida JSON
   */
  private validateJSON(): boolean {
    this.jsonError = '';

    if (!this.jsonInput.trim()) {
      this.jsonError = 'Por favor, cole um JSON de exemplo';
      return false;
    }

    try {
      //  Sanitizar antes de parsear
      const sanitized = this.sanitizeJSON(this.jsonInput);
      JSON.parse(sanitized);
      return true;
    } catch (error) {
      this.jsonError = 'JSON inválido: ' + (error as Error).message;
      return false;
    }
  }

  /**
   * Analisa JSON
   */
  private analyzeJSON() {
    try {
      //  Sanitizar antes de parsear
      const sanitized = this.sanitizeJSON(this.jsonInput);
      const json = JSON.parse(sanitized);
      this.analysisResult = this.analyzerService.analyzeJSON(json);

      // Selecionar todas as entidades por padrão
      this.config.selectedEntities = this.analysisResult.entities.map(e => e.name);

      console.log('📊 Análise completa:', this.analysisResult);
      this.message.success('JSON analisado com sucesso!');
    } catch (error) {
      this.message.error('Erro ao analisar JSON: ' + (error as Error).message);
      this.currentStep = 0;
    }
  }

  /**
   * Gera tela
   */
  generate() {
    if (!this.analysisResult) return;

    this.message.loading('Gerando tela...', { nzDuration: 0 });

    setTimeout(() => {
      //  NOVO: Passar config junto com analysisResult
      const resultWithConfig = {
        ...this.analysisResult!,
        config: this.config
      };
      
      this.screenGenerated.emit(resultWithConfig);
      this.message.remove();
      this.message.success('Tela gerada com sucesso!');
      this.close();
    }, 1000);
  }

  /**
   * Carrega template de exemplo
   */
  loadSampleJSON(template: 'customers' | 'products' | 'orders') {
    const samples = {
      customers: `{
  "data": {
    "customers": [
      {
        "id": 1,
        "name": "João Silva",
        "email": "joao@email.com",
        "phone": "+55 11 99999-9999",
        "address": {
          "street": "Rua ABC, 123",
          "city": "São Paulo",
          "state": "SP",
          "zipCode": "01234-567"
        },
        "active": true,
        "createdAt": "2025-01-15"
      }
    ]
  }
}`,
      products: `{
  "data": {
    "products": [
      {
        "id": 1,
        "name": "Notebook Dell",
        "description": "Notebook Dell Inspiron 15 com processador Intel i7",
        "category": "Eletrônicos",
        "price": 3500.00,
        "stock": 15,
        "active": true,
        "createdAt": "2025-01-10"
      }
    ]
  }
}`,
      orders: `{
  "data": {
    "orders": [
      {
        "id": 1001,
        "customerId": 1,
        "customerName": "João Silva",
        "orderDate": "2025-01-15",
        "total": 3500.00,
        "status": "Pending",
        "items": [
          {
            "productId": 1,
            "productName": "Notebook Dell",
            "quantity": 1,
            "price": 3500.00
          }
        ]
      }
    ]
  }
}`
    };

    this.jsonInput = samples[template];
  }
}


